import React, { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import styled from '@emotion/styled'
import { CircularProgress } from '@mui/material'
import Box from '../components/Box'
import { getGrooverText } from '../libs/GrooverTextRepository'
import { PrivateLessonOrder, Client } from '../../../pure-js/types/GrooverTypes'
import { getPrivateLessonOrder } from '../libs/CloudFunctionsApiHandler'
import { getClient } from '../libs/DBApiHandler'
import { getDefaultInstructorName } from '../../../pure-js/libs/PrivateLessonHelpers'
import { useClientRouteHost } from '../context/ClientRouteHostContext'

export const PrivateLessonOrderPage: React.FC = () => {
  const { orderId } = useParams<{ orderId: string }>()
  const { clientId } = useClientRouteHost()
  const [order, setOrder] = useState<PrivateLessonOrder | null>(null)
  const [client, setClient] = useState<Client | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  useEffect(() => {
    if (!clientId || !orderId) return

    setLoading(true)
    Promise.all([getPrivateLessonOrder({ clientId, orderId }), getClient(clientId)])
      .then(([orderResponse, clientResponse]) => {
        setOrder(orderResponse)
        setClient(clientResponse || null)
        setError(null)
      })
      .catch((err) => {
        setError(err?.message || getGrooverText('privateLessonsOrderFailedToLoad') || 'Failed to load order')
        setOrder(null)
      })
      .finally(() => setLoading(false))
  }, [clientId, orderId])

  if (loading) {
    return (
      <PageWrapper align="center" justify="center">
        <CircularProgress />
      </PageWrapper>
    )
  }

  if (error || !order) {
    return (
      <PageWrapper align="center" justify="center">
        <ErrorText>{error || getGrooverText('privateLessonsOrderNotFound') || 'Order not found'}</ErrorText>
      </PageWrapper>
    )
  }

  return (
    <PageWrapper align="center" justify="center">
      <Card>
        <Header>
          <Title>{getGrooverText('privateLessonsOrderTitle') || 'Private lesson order'}</Title>
          <SubTitle>
            {client?.name || order.schoolName || getGrooverText('privateLessonsPortalSubTitle') || 'Private lessons'}
          </SubTitle>
        </Header>

        <OrderRow>
          <Label>{getGrooverText('privateLessonsCheckoutPackage') || 'Package'}</Label>
          <Value>{order.packageName || order.packageId}</Value>
        </OrderRow>
        <OrderRow>
          <Label>{getGrooverText('privateLessonsCheckoutInstructor') || 'Instructor'}</Label>
          <Value>
            {getDefaultInstructorName(order) ||
              getGrooverText('privateLessonsCheckoutAllInstructors') ||
              'All instructors'}
          </Value>
        </OrderRow>
        <OrderRow>
          <Label>{getGrooverText('adminGeneralPrivateLessonsTableRemaining') || 'Remaining'}</Label>
          <Value>
            {order.lessonCount - order.lessonsUsed}/{order.lessonCount}
          </Value>
        </OrderRow>
        <OrderRow>
          <Label>{getGrooverText('adminGeneralPrivateLessonsOrderStatusLabel') || 'Status'}</Label>
          <Value>{getGrooverText(`privateLessonsOrderStatus_${order.status}`) || order.status}</Value>
        </OrderRow>
        <OrderRow>
          <Label>{getGrooverText('privateLessonsCheckoutPageYourOrderId') || 'Order ID'}</Label>
          <Value>{order.id}</Value>
        </OrderRow>

        <InfoBox>
          {getGrooverText('privateLessonsOrderNextSteps') || 'The school will contact you to schedule your lessons.'}
        </InfoBox>
      </Card>
    </PageWrapper>
  )
}

const PageWrapper = styled(Box)`
  min-height: 100vh;
  padding: 32px 16px;
  background: #0f1117;
`

const Card = styled.div`
  width: min(720px, 100%);
  background: rgba(24, 28, 36, 0.95);
  border-radius: 16px;
  padding: 28px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.4);
  border: 1px solid rgba(124, 58, 237, 0.2);
  color: #fff;
`

const Header = styled.div`
  margin-bottom: 24px;
`

const Title = styled.div`
  font-size: 24px;
  font-weight: 600;
`

const SubTitle = styled.div`
  margin-top: 8px;
  color: rgba(255, 255, 255, 0.7);
`

const OrderRow = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 10px 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
`

const Label = styled.div`
  color: rgba(255, 255, 255, 0.7);
`

const Value = styled.div`
  font-weight: 600;
`

const InfoBox = styled.div`
  margin-top: 24px;
  padding: 16px;
  border-radius: 12px;
  background: rgba(124, 58, 237, 0.1);
  color: rgba(255, 255, 255, 0.85);
`

const ErrorText = styled.div`
  color: #ff6b6b;
`

export default PrivateLessonOrderPage
